import { useState, ReactElement, ReactNode } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_USERS_SUGGESTIONS } from 'features/Users/queries-legacy'
import { GTUserSuggestion, GTUserSuggestionsResponse } from 'gql/types.generated'
import { EUserType } from 'features/Users/types'

const messages = defineMessages({
  error: {
    id: 'user.autocomplete.error',
    defaultMessage: 'There was an issue while loading your users'
  },
  empty: {
    id: 'user.autocomplete.empty',
    defaultMessage: 'No users found'
  }
})

interface IUserAutocompleteProps {
  companyId?: string
  getItemLabel?: (user: GTUserSuggestion) => string
  placeholder: string
  value: string
  label?: ReactNode
  onChange: (user: GTUserSuggestion) => void
  onBlur?: () => void
  touched?: boolean
  errors?: string
  optional?: boolean
  isAssigned?: boolean
  type?: EUserType
  additionalUsers?: GTUserSuggestion[]
}

export const UserAutocomplete = ({
  companyId,
  getItemLabel,
  placeholder,
  value,
  label,
  onChange,
  isAssigned,
  type,
  additionalUsers = [],
  ...inputProps
}: IUserAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getUsers, { data, loading, error }] = useLazyQuery<{ userSuggestions: GTUserSuggestionsResponse }>(
    GET_USERS_SUGGESTIONS
  )
  const { isDebouncing } = useDebouncedQuery({
    query: getUsers,
    pattern,
    extraInputs: {
      companyID: companyId,
      isAssigned,
      type
    }
  })

  let users = data?.userSuggestions.items || []
  if (!isEmpty(additionalUsers)) {
    users = [...additionalUsers, ...users]
  }

  const handleChange = (user: GTUserSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)

    if (!isEmpty(user)) {
      onChange(user)
    } else {
      onChange({
        id: '',
        username: '',
        firstName: '',
        lastName: '',
        name: ''
      })
    }
  }

  return (
    <Autocomplete
      placeholder={placeholder}
      value={value}
      label={label}
      items={users}
      getItemLabel={(user: GTUserSuggestion): string =>
        getItemLabel ? getItemLabel(user) : `${user.firstName} ${user.lastName} (${user.username})`
      }
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      {...inputProps}
    />
  )
}
